import { useState } from "react";
import {
    Box,
    Button,
    Chip,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";

import {
    currencyFormatter,
    formatChannelLabel,
    getChannelChipStyles,
    getOrderStatusChipStyles,
} from "../../../lib/presentation";
import { exportDocumentHistoryPdf, exportYearlyStatsPdf } from "./pdfExport";
import type { ClientExpoClientObjectRow } from "../../../lib/api/types";
import type { HistoryIntervalRow, HistoryYearlyStat, TransportHistoryRow } from "./types";
import TransportHistoryCard from "./TransportHistoryCard";
import ExpoClientObjectsCard from "./ExpoClientObjectsCard";
import { formatDate, formatDaysInterval } from "./utils";

export type ClientHistoryViewTab = "documents" | "transport" | "expo-objects";

interface MainContentProps {
    activeTab: ClientHistoryViewTab;
    intervals: HistoryIntervalRow[];
    onTabChange: (tab: ClientHistoryViewTab) => void;
    yearlyStats: HistoryYearlyStat[];
    transportHistory: TransportHistoryRow[];
    transportHistorySource?: "transport-db" | "orders" | "none";
    expoClientObjects?: ClientExpoClientObjectRow[];
}

export default function MainContent({
    activeTab,
    intervals,
    onTabChange,
    yearlyStats,
    transportHistory,
    transportHistorySource = "none",
    expoClientObjects = [],
}: MainContentProps) {
    const [historyPage, setHistoryPage] = useState(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

    const paginatedIntervals = intervals.slice(
        historyPage * historyRowsPerPage,
        historyPage * historyRowsPerPage + historyRowsPerPage
    );

    return (
        <Stack spacing={2}>
            <Tabs
                value={activeTab}
                onChange={(_, value: ClientHistoryViewTab) => onTabChange(value)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
            >
                <Tab value="documents" label="История по документ" />
                <Tab value="transport" label="Транспортна история" />
                <Tab value="expo-objects" label="Обекти на клиенти" />
            </Tabs>

            {activeTab === "documents" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 0.85fr" }, gap: 2 }}>
                <Paper className="table-card" elevation={0}>
                    <Box className="table-card__header" sx={{ display: "flex", gap: 1.5, justifyContent: "space-between", alignItems: { sm: "center" }, flexDirection: { xs: "column", sm: "row" } }}>
                        <Box>
                            <Typography variant="h5">История по документи</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Всяка заявка с дата, канал, сума и интервал от предишната.
                            </Typography>
                        </Box>
                        <Button variant="outlined" size="small" onClick={() => exportDocumentHistoryPdf(intervals)}>
                            PDF таблица
                        </Button>
                    </Box>
                    <Box className="table-card__scroller">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>Документ</TableCell>
                                    <TableCell>Шофьор / Бус</TableCell>
                                    <TableCell>Канал</TableCell>
                                    <TableCell>Статус</TableCell>
                                    <TableCell align="right">Сума</TableCell>
                                    <TableCell align="right">Интервал</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedIntervals.map(({ order, intervalDays }) => (
                                    <TableRow key={`${order.orderNumber}-${order.createdAt}`} hover>
                                        <TableCell>{formatDate(order.createdAt)}</TableCell>
                                        <TableCell>{order.orderNumber}</TableCell>
                                        <TableCell>
                                            {order.deliveryTransport || order.deliveryDriver || order.deliveryBus ? (
                                                <Stack spacing={0.25}>
                                                    <Typography sx={{ fontWeight: 700 }}>
                                                        {order.deliveryTransport || order.deliveryDriver || "—"}
                                                    </Typography>
                                                    {order.deliveryRoute ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {order.deliveryRoute}
                                                        </Typography>
                                                    ) : null}
                                                </Stack>
                                            ) : (
                                                <Typography color="text.secondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatChannelLabel(order.channel)}
                                                size="small"
                                                sx={{ fontWeight: 700, ...getChannelChipStyles(order.channel) }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={order.status}
                                                size="small"
                                                sx={{ fontWeight: 700, ...getOrderStatusChipStyles(order.status) }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{currencyFormatter.format(order.value)}</TableCell>
                                        <TableCell align="right">{formatDaysInterval(intervalDays)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <TablePagination
                        component="div"
                        count={intervals.length}
                        page={historyPage}
                        onPageChange={(_, nextPage) => setHistoryPage(nextPage)}
                        rowsPerPage={historyRowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setHistoryRowsPerPage(Number(event.target.value));
                            setHistoryPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="Редове"
                    />
                </Paper>

                    <Stack spacing={2}>
                        <Paper className="table-card" elevation={0}>
                        <Box className="table-card__header" sx={{ display: "flex", gap: 1.5, justifyContent: "space-between", alignItems: { sm: "center" }, flexDirection: { xs: "column", sm: "row" } }}>
                            <Box>
                                <Typography variant="h5">Годишен разчет</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Общо по години: брой документи, сума и средна стойност.
                                </Typography>
                            </Box>
                            <Button variant="outlined" size="small" onClick={() => exportYearlyStatsPdf(yearlyStats)}>
                                PDF таблица
                            </Button>
                        </Box>
                        <Box className="table-card__scroller">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Година</TableCell>
                                        <TableCell align="right">Документи</TableCell>
                                        <TableCell align="right">Обща сума</TableCell>
                                        <TableCell align="right">Средно</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {yearlyStats.map((row) => (
                                        <TableRow key={row.year}>
                                            <TableCell>{row.year}</TableCell>
                                            <TableCell align="right">{row.orders}</TableCell>
                                            <TableCell align="right">{currencyFormatter.format(row.totalValue)}</TableCell>
                                            <TableCell align="right">{currencyFormatter.format(row.averageValue)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Paper>

                    <PaymentInfoCard />
                </Stack>
            </Box>
            ) : activeTab === "transport" ? (
                <TransportHistoryCard
                    rows={transportHistory}
                    source={transportHistorySource}
                />
            ) : (
                <ExpoClientObjectsCard rows={expoClientObjects} />
            )}
        </Stack>
    );
}

function PaymentInfoCard() {
    return (
        <Paper className="table-card" elevation={0}>
            <Stack spacing={1.5}>
                <Box className="table-card__header">
                    <Typography variant="h5">Платежен прочит</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Какво може и какво не може да се изведе от текущия прозорец.
                    </Typography>
                </Box>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid rgba(216, 225, 235, 0.9)",
                        bgcolor: "rgba(248, 250, 252, 0.86)",
                    }}
                >
                    <Typography variant="overline" color="primary.main">Редовен платец</Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>Няма директни данни за плащане</Typography>
                </Box>
            </Stack>
        </Paper>
    );
}
